import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleOnSettled } from '@utils/formHelpers';
import { useCreateNote } from '@services/notes/create';
import { useUpdateNote } from '@services/notes/update';
import { DefaultApiResp } from '@root/globalTypes';
import { useRemoveNote } from '@services/notes/remove';
import { useErrors } from '@hooks/errors';
import { NoteFormProps, NoteFormValues } from './types';
import { defaultValues, formSchema } from './constants';
import { FormProps } from '@forms/types';
import { useAccess } from '@hooks/access';

export const useNoteForm = ({
  initialValues,
  setOpen,
  successHandler,
  usage,
}: FormProps<NoteFormProps>) => {
  const access = useAccess();

  const handleErrors = useErrors();

  const form = useForm<NoteFormValues>({
    resolver: zodResolver(formSchema({ usage, id: initialValues?.id })),
    defaultValues,
  });

  const { setError, watch } = form;

  const noteContent = watch('content');
  const isEmptyLocked = access.fieldUser && !noteContent.length;

  const callback = (resp: DefaultApiResp) => {
    handleOnSettled<DefaultApiResp>(resp, successHandler, () =>
      handleErrors(resp.errors, (key, message) =>
        setError(key as keyof NoteFormValues, { message }),
      ),
    );
  };

  const create = useCreateNote({ callback });
  const update = useUpdateNote({ callback });
  const remove = useRemoveNote({ callback });

  const onSubmit = async ({ important, content }: NoteFormValues) => {
    if (!initialValues || isEmptyLocked) return;
    setOpen(false);
    const { id, jobId, typeId } = initialValues;
    if (!id) {
      if (usage) {
        create.mutate({
          jobId,
          referableType: 'Job',
          referableId: jobId,
          content,
          usage,
        });
      } else {
        create.mutate({
          jobId,
          referableType: important ? 'Job' : initialValues.referableType,
          referableId: important ? jobId : typeId,
          content,
        });
      }
    } else if (!content) {
      remove.mutate({
        id,
        jobId,
        referableType: important ? 'Job' : initialValues.referableType,
        referableId: important ? jobId : typeId,
      });
    } else
      update.mutate({
        id,
        content,
        referableType: important ? 'Job' : initialValues.referableType,
        referableId: important ? jobId : typeId,
      });
  };

  return {
    form,
    onSubmit,
    locked: create.isLoading || update.isLoading || isEmptyLocked,
  };
};
